import React from 'react';
import { MapPin } from 'lucide-react';
import { PinValidation } from '@gps-hunt/shared';

interface GpsAccuracyIndicatorProps {
  accuracy: number | null;
  isLoading?: boolean;
  className?: string;
}

type AccuracyLevel = 'good' | 'fair' | 'poor' | 'unknown';

/**
 * GPS accuracy indicator component
 * Shows color-coded accuracy level:
 * - Green (≤10m): Good accuracy
 * - Yellow (≤20m): Fair accuracy (minimum for pin placement)
 * - Red (>20m): Poor accuracy (cannot place pins)
 */
export const GpsAccuracyIndicator: React.FC<GpsAccuracyIndicatorProps> = ({
  accuracy,
  isLoading = false,
  className = '',
}) => {
  const getAccuracyLevel = (acc: number | null): AccuracyLevel => {
    if (acc === null) return 'unknown';
    if (acc <= 10) return 'good';
    if (acc <= PinValidation.accuracy.max) return 'fair';
    return 'poor';
  };

  const level = getAccuracyLevel(accuracy);

  const colorClasses: Record<AccuracyLevel, string> = {
    good: 'bg-success/20 text-success border-success/50',
    fair: 'bg-warning/20 text-warning border-warning/50',
    poor: 'bg-primary/20 text-primary border-primary/50',
    unknown: 'bg-text-secondary/20 text-text-secondary border-text-secondary/50',
  };

  const iconColors: Record<AccuracyLevel, string> = {
    good: 'text-success',
    fair: 'text-warning',
    poor: 'text-primary',
    unknown: 'text-text-secondary',
  };

  const statusMessages: Record<AccuracyLevel, string> = {
    good: 'GPS accuracy is excellent',
    fair: 'GPS accuracy is acceptable',
    poor: 'GPS accuracy too low - move to open area',
    unknown: 'Acquiring GPS signal...',
  };

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${colorClasses[level]} ${className}`}
      role="status"
      aria-label={statusMessages[level]}
    >
      {isLoading ? (
        <div className="w-5 h-5 animate-spin border-2 border-current border-t-transparent rounded-full" />
      ) : (
        <MapPin className={`w-5 h-5 ${iconColors[level]}`} />
      )}
      
      <span className="font-mono text-sm">
        {accuracy !== null ? `${accuracy.toFixed(1)}m` : '—'}
      </span>

      {/* Pulsing indicator for good accuracy */}
      {level === 'good' && !isLoading && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
        </span>
      )}
    </div>
  );
};

/**
 * Compact version for use in headers
 */
export const GpsAccuracyBadge: React.FC<GpsAccuracyIndicatorProps> = ({
  accuracy,
  isLoading = false,
  className = '',
}) => {
  const getColor = (acc: number | null): string => {
    if (acc === null) return 'text-text-secondary';
    if (acc <= 10) return 'text-success';
    if (acc <= PinValidation.accuracy.max) return 'text-warning';
    return 'text-primary';
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {isLoading ? (
        <div className="w-4 h-4 animate-spin border-2 border-accent-blue border-t-transparent rounded-full" />
      ) : (
        <MapPin className={`w-4 h-4 ${getColor(accuracy)}`} />
      )}
      <span className={`font-mono text-sm ${getColor(accuracy)}`}>
        {accuracy !== null ? `${accuracy.toFixed(1)}m` : '...'}
      </span>
    </div>
  );
};

export default GpsAccuracyIndicator;
